import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BadgeCheck, Menu, X, Truck, UserRound } from "lucide-react";
import { Logo } from "./Logo";
import { useCustomer } from "../lib/auth";
import { ReviewsBadge } from "./Reviews";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/meal-prep", label: "Meal Prep", testid: "nav-link-meal-prep" },
  { to: "/catering", label: "Catering", testid: "nav-link-catering" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export const HalalBadge = ({ className = "" }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full bg-halal/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-halal ${className}`}
    data-testid="halal-badge"
  >
    <BadgeCheck size={14} strokeWidth={2.5} />
    100% Certified Halal
  </span>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useCustomer();
  const accountLabel = user ? user.name.split(" ")[0] : "Sign in";

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="bg-spice text-parchment" data-testid="promo-banner">
        <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-4 py-2 text-center font-mono text-[11px] tracking-wide sm:text-xs">
          <Truck size={13} className="shrink-0" />
          <span>Delivery only across the DMV — no pickup</span>
          <span className="hidden sm:inline">·</span>
          <span className="hidden font-semibold sm:inline">15% off first order · code HIPLATE15</span>
        </div>
      </div>
      <nav className="border-b border-ink/10 bg-parchment/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8">
          <Link to="/" data-testid="nav-logo" aria-label="HiPlate home">
            <Logo />
          </Link>
          <div className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors duration-300 hover:text-spice ${
                    isActive ? "text-spice" : "text-ink/70"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
          <div className="hidden items-center gap-4 lg:flex">
            <ReviewsBadge />
            <HalalBadge />
            <Link
              to="/account"
              data-testid="nav-account-link"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-ink/70 transition-colors duration-300 hover:text-spice"
            >
              <UserRound size={16} /> {accountLabel}
            </Link>
            <button
              data-testid="nav-order-now-btn"
              onClick={() => navigate("/meal-prep")}
              className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-parchment transition-all duration-300 hover:bg-spice hover:shadow-lg hover:shadow-spice/30"
            >
              Order Now
            </button>
          </div>
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden border-t border-ink/10 bg-parchment/95 backdrop-blur-xl lg:hidden"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col gap-1 px-4 py-4">
                {LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-3 text-base font-medium text-ink/80 hover:bg-cream"
                  >
                    {l.label}
                  </NavLink>
                ))}
                <NavLink
                  to="/account"
                  data-testid="mobile-nav-account-link"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 rounded-lg px-3 py-3 text-base font-medium text-ink/80 hover:bg-cream"
                >
                  <UserRound size={18} /> {user ? `My account (${accountLabel})` : "Sign in / My account"}
                </NavLink>
                <div className="mt-2 flex flex-wrap items-center gap-3 px-3">
                  <ReviewsBadge onNavigate={() => setOpen(false)} />
                  <HalalBadge />
                </div>
                <button
                  data-testid="mobile-order-now-btn"
                  onClick={() => {
                    setOpen(false);
                    navigate("/meal-prep");
                  }}
                  className="mt-3 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-parchment"
                >
                  Order Now
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};
